import React, { useEffect, useState } from "react";
import api, { apiErrorMsg, formatINR } from "../lib/api";
import { CreditCard, CheckCircle, AlertCircle, RotateCcw, AlertTriangle, XCircle, Webhook } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  success: { cls: "bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]", icon: CheckCircle, label: "Success" },
  refunded: { cls: "bg-[#FECACA] text-[#991B1B] border-[#F87171]", icon: RotateCcw, label: "Refunded" },
  disputed: { cls: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]", icon: AlertTriangle, label: "Disputed" },
  failed: { cls: "bg-[#E5E7EB] text-[#374151] border-[#D1D5DB]", icon: XCircle, label: "Failed" },
};

/**
 * Admin Transactions Tab (v2.19)
 * Audit log of every subscription payment via Razorpay.
 */
export default function AdminTransactionsTab() {
  const [txns, setTxns] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/admin/transactions").then((r) => setTxns(r.data || [])),
      api.get("/admin/webhook-events").then((r) => setEvents(r.data || [])).catch(() => {}),
    ])
      .catch((e) => toast.error(apiErrorMsg(e, "Failed to load transactions")))
      .finally(() => setLoading(false));
  }, []);

  const total = txns.reduce(
    (sum, t) => sum + (t.is_demo || t.status !== "success" ? 0 : (t.amount_inr || 0)),
    0
  );

  return (
    <div className="space-y-4" data-testid="admin-transactions-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <CreditCard size={16} className="text-[#0055FF]" />
          <h2 className="font-heading font-bold text-xl tracking-tight">Transactions</h2>
          <span className="text-[11px] text-[#A1A1AA]">Razorpay payments audit log</span>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B]">Lifetime revenue (live only)</div>
          <div className="font-heading font-bold text-2xl text-[#0055FF] font-mono-data" data-testid="lifetime-revenue">{formatINR(total)}</div>
        </div>
      </div>

      {loading ? (
        <div className="text-[#A1A1AA] text-sm p-6 text-center bg-white border border-dashed border-[#E4E4E7] rounded-sm">Loading…</div>
      ) : txns.length === 0 ? (
        <div className="text-[#A1A1AA] text-sm p-10 text-center bg-white border border-dashed border-[#E4E4E7] rounded-sm">
          No transactions yet. Subscriptions purchased through Razorpay will appear here.
        </div>
      ) : (
        <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden" data-testid="transactions-table">
          <table className="w-full text-[13px]">
            <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#52525B] font-semibold">
              <tr>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">User</th>
                <th className="text-left px-4 py-3">Plan</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Payment ID</th>
                <th className="text-center px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {txns.map((t) => (
                <tr key={t.id} className="border-t border-[#F4F4F5]" data-testid={`txn-${t.id}`}>
                  <td className="px-4 py-3 text-[12px] text-[#52525B] whitespace-nowrap">
                    {new Date(t.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{t.user_name || t.user_email}</div>
                    <div className="text-[11px] text-[#A1A1AA]">{t.user_email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{t.plan_name}</div>
                    <div className="text-[11px] text-[#A1A1AA] font-mono">{t.plan_id}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono-data font-semibold text-[#0055FF]">
                    {formatINR(t.amount_inr)}
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px] text-[#52525B]">{t.payment_id || "—"}</td>
                  <td className="px-4 py-3 text-center">
                    {t.is_demo ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A] px-2 py-0.5 rounded-sm">
                        <AlertCircle size={9} /> Demo
                      </span>
                    ) : (() => {
                      const s = t.status || "success";
                      const cfg = STATUS_BADGE[s] || STATUS_BADGE.success;
                      const Icon = cfg.icon;
                      return (
                        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold border px-2 py-0.5 rounded-sm ${cfg.cls}`}>
                          <Icon size={9} /> {cfg.label}
                        </span>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Webhook events panel */}
      <div className="mt-6">
        <div className="flex items-center gap-2 mb-2">
          <Webhook size={14} className="text-[#0055FF]" />
          <h3 className="font-heading font-semibold text-[15px] tracking-tight">Razorpay Webhook Events</h3>
          <span className="text-[11px] text-[#A1A1AA]">last {events.length} events</span>
        </div>
        <div className="bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm p-3 text-[12px] text-[#52525B] mb-3">
          <div className="font-semibold text-[#09090B]">Setup:</div>
          <ol className="list-decimal ml-5 mt-1 space-y-0.5">
            <li>Razorpay Dashboard → Settings → Webhooks → Add new</li>
            <li>URL: <code className="font-mono bg-white border border-[#E4E4E7] rounded-sm px-1.5">{typeof window !== "undefined" ? `${window.location.origin}/api/razorpay/webhook` : "/api/razorpay/webhook"}</code></li>
            <li>Events: <span className="font-mono">payment.captured, payment.failed, refund.created, refund.processed, payment.dispute.created</span></li>
            <li>Set <code className="font-mono">RAZORPAY_WEBHOOK_SECRET</code> on the server's env vars to the secret Razorpay generates.</li>
          </ol>
        </div>
        {events.length === 0 ? (
          <div className="text-[#A1A1AA] text-[12px] p-4 text-center bg-white border border-dashed border-[#E4E4E7] rounded-sm">
            No webhook events received yet.
          </div>
        ) : (
          <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden" data-testid="webhook-events-table">
            <table className="w-full text-[12px]">
              <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#52525B] font-semibold">
                <tr>
                  <th className="text-left px-3 py-2">Received</th>
                  <th className="text-left px-3 py-2">Event Type</th>
                  <th className="text-left px-3 py-2">Event ID</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.event_id} className="border-t border-[#F4F4F5]" data-testid={`webhook-event-${e.event_id}`}>
                    <td className="px-3 py-2 whitespace-nowrap text-[#52525B]">{new Date(e.received_at).toLocaleString()}</td>
                    <td className="px-3 py-2 font-semibold">{e.event_type}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-[#52525B]">{e.event_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
